/**
 * CanvasGridElement - A draggable and resizable element in the canvas grid
 * Handles selection, drag, resize, and displays element content
 */

import React, { memo, useState, useCallback, useRef, useEffect } from "react";
import { Box, IconButton, Stack } from "@mui/joy";
import { useDraggable } from "@dnd-kit/core";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import type {
  CanvasGridConfig,
  ResizeDirection,
  ResizeState,
  CanvasElementType,
} from "../../types";
import { ELEMENT_MIN_SIZES } from "../../types";
import { CANVAS_DROP_TARGET_SHADOW } from "../../theme/canvasTokens";

interface CanvasGridElementProps {
  id: string;
  gridColumn: number;
  gridRow: number;
  columnSpan: number;
  rowSpan: number;
  isSelected: boolean;
  isHovered: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
  mode: "edit" | "preview";
  config: CanvasGridConfig;
  label?: string;
  elementType: string;
  isBound?: boolean;
  onSelect: (additive?: boolean) => void;
  onHover: (isHovered: boolean) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onResize: (columnSpan: number, rowSpan: number) => void;
  children?: React.ReactNode;
}

function CanvasGridElementComponent({
  id,
  gridColumn,
  gridRow,
  columnSpan,
  rowSpan,
  isSelected,
  isHovered,
  isDragging,
  isDropTarget,
  mode,
  config,
  label,
  elementType,
  isBound,
  onSelect,
  onHover,
  onDelete,
  onDuplicate,
  onResize,
  children,
}: CanvasGridElementProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    direction: null,
    startX: 0,
    startY: 0,
    startColumnSpan: columnSpan,
    startRowSpan: rowSpan,
  });
  const [previewSpans, setPreviewSpans] = useState<{
    columnSpan: number;
    rowSpan: number;
  } | null>(null);

  // dnd-kit draggable
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    data: {
      type: "canvas-element",
      id,
      gridColumn,
      gridRow,
      columnSpan,
      rowSpan,
    },
    disabled: mode === "preview",
  });

  // Combine refs
  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      (elementRef as React.MutableRefObject<HTMLDivElement | null>).current =
        node;
    },
    [setNodeRef],
  );

  // Calculate cell dimensions for resize
  const getCellDimensions = useCallback(() => {
    if (!elementRef.current)
      return { cellWidth: 0, cellHeight: config.rowHeight };
    const parentWidth = elementRef.current.parentElement?.clientWidth || 0;
    const cellWidth =
      (parentWidth - (config.columns - 1) * config.gap) / config.columns;
    return { cellWidth, cellHeight: config.rowHeight };
  }, [config]);

  // Resize handlers
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, direction: ResizeDirection) => {
      e.stopPropagation();
      e.preventDefault();

      setResizeState({
        isResizing: true,
        direction,
        startX: e.clientX,
        startY: e.clientY,
        startColumnSpan: columnSpan,
        startRowSpan: rowSpan,
      });
    },
    [columnSpan, rowSpan],
  );

  // Handle mouse move during resize
  useEffect(() => {
    if (!resizeState.isResizing) return;

    // Get min sizes for this element type
    const minSizes = ELEMENT_MIN_SIZES[elementType as CanvasElementType] || {
      minRowSpan: 1,
      minColumnSpan: 1,
    };

    const handleMouseMove = (e: MouseEvent) => {
      const { cellWidth, cellHeight } = getCellDimensions();
      if (cellWidth === 0) return;

      const deltaX = e.clientX - resizeState.startX;
      const deltaY = e.clientY - resizeState.startY;

      let newColumnSpan = resizeState.startColumnSpan;
      let newRowSpan = resizeState.startRowSpan;

      // Calculate column span change (for 'e' and 'se' directions)
      if (resizeState.direction === "e" || resizeState.direction === "se") {
        const colDelta = Math.round(deltaX / (cellWidth + config.gap));
        newColumnSpan = Math.max(
          minSizes.minColumnSpan,
          Math.min(
            resizeState.startColumnSpan + colDelta,
            config.columns - gridColumn + 1, // Don't exceed grid bounds
          ),
        );
      }

      // Calculate row span change (for 's' and 'se' directions)
      if (resizeState.direction === "s" || resizeState.direction === "se") {
        const rowDelta = Math.round(deltaY / (cellHeight + config.gap));
        newRowSpan = Math.max(
          minSizes.minRowSpan,
          resizeState.startRowSpan + rowDelta,
        );
      }

      setPreviewSpans({ columnSpan: newColumnSpan, rowSpan: newRowSpan });
    };

    const handleMouseUp = () => {
      if (previewSpans) {
        onResize(previewSpans.columnSpan, previewSpans.rowSpan);
      }
      setResizeState({
        isResizing: false,
        direction: null,
        startX: 0,
        startY: 0,
        startColumnSpan: columnSpan,
        startRowSpan: rowSpan,
      });
      setPreviewSpans(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    resizeState,
    previewSpans,
    config,
    gridColumn,
    getCellDimensions,
    onResize,
    columnSpan,
    rowSpan,
    elementType,
  ]);

  const showControls = mode === "edit" && (isSelected || isHovered);
  const showResizeHandles = mode === "edit" && isSelected;

  // Calculate display spans (use preview during resize)
  const displayColumnSpan = previewSpans?.columnSpan ?? columnSpan;
  const displayRowSpan = previewSpans?.rowSpan ?? rowSpan;

  // Transform style for drag preview
  const dragStyle = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <Box
      ref={combinedRef}
      data-canvas-element-id={id}
      sx={{
        gridColumn: `${gridColumn} / span ${displayColumnSpan}`,
        gridRow: `${gridRow} / span ${displayRowSpan}`,
        position: "relative",
        minHeight: config.rowHeight,
        borderRadius: "md",
        border: mode === "edit" ? "1px solid" : "none",
        borderColor: isDropTarget
          ? "success.500"
          : isSelected
            ? "primary.500"
            : isHovered
              ? "primary.300"
              : "neutral.outlinedBorder",
        borderWidth: isDropTarget ? 2 : 1,
        bgcolor: isDropTarget
          ? "success.softBg"
          : isSelected
            ? "primary.softBg"
            : isDragging
              ? "background.level2"
              : "background.surface",
        boxShadow: isDropTarget ? CANVAS_DROP_TARGET_SHADOW : undefined,
        opacity: isDragging ? 0.5 : 1,
        cursor: mode === "edit" ? "pointer" : "default",
        transition: resizeState.isResizing
          ? "none"
          : "border-color 0.15s, background-color 0.15s, opacity 0.15s",
        userSelect: "none",
        zIndex: isSelected || isDragging ? 100 : 1,
        // Visible keyboard focus indicator (spec section 9). A grid element is
        // Tab-reachable in edit mode; the focus ring makes the current element
        // clear to a keyboard-only user before they select and move it.
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.500",
          outlineOffset: 2,
        },
        ...dragStyle,
      }}
      // Keyboard authoring (spec section 9 / R26): in edit mode each element is
      // focusable and can be selected with Enter or Space. Selecting reveals the
      // drag handle, which dnd-kit makes keyboard-operable (Space to pick up,
      // arrow keys to move one cell, Space to drop). The guard ignores key events
      // that bubbled up from a child (such as the drag handle mid-drag) so the two
      // handlers never both fire on the same press.
      tabIndex={mode === "edit" ? 0 : undefined}
      role={mode === "edit" ? "button" : undefined}
      aria-pressed={mode === "edit" ? isSelected : undefined}
      aria-label={
        mode === "edit"
          ? `Canvas element: ${label || elementType}. Press Enter to select.`
          : undefined
      }
      onKeyDown={(e) => {
        if (mode !== "edit") return;
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(e.shiftKey || e.metaKey);
        }
      }}
      onClick={(e) => {
        if (mode === "edit") {
          e.stopPropagation();
          onSelect(e.shiftKey || e.metaKey);
        }
      }}
      onMouseEnter={() => mode === "edit" && onHover(true)}
      onMouseLeave={() => mode === "edit" && onHover(false)}
    >
      {/* Element Controls (drag handle, duplicate, delete) */}
      {showControls && (
        <Stack
          data-testid={`${id}-controls`}
          direction="row"
          spacing={0.5}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            bgcolor: "background.surface",
            borderRadius: "sm",
            boxShadow: "sm",
            p: 0.25,
            zIndex: 200,
          }}
        >
          {/* Drag Handle */}
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            aria-label={`Move ${label || elementType}`}
            {...listeners}
            {...attributes}
            sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          {/* Duplicate */}
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          {/* Delete */}
          <IconButton
            size="sm"
            variant="plain"
            color="danger"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}

      {/* Element Content */}
      <Box
        sx={{
          p: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>

      {/* Resize Handles */}
      {showResizeHandles && (
        <>
          {/* East (right edge) */}
          <Box
            data-testid={`${id}-resize-east`}
            aria-label={`Resize ${label || elementType} width`}
            sx={{
              position: "absolute",
              top: "50%",
              right: 2,
              transform: "translateY(-50%)",
              width: 10,
              height: 32,
              bgcolor: "primary.500",
              borderRadius: 4,
              zIndex: 201,
              cursor: "e-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": { bgcolor: "primary.600" },
            }}
            onMouseDown={(e) => handleResizeStart(e, "e")}
          >
            <Box
              sx={{
                width: 2,
                height: 16,
                bgcolor: "white",
                borderRadius: 1,
              }}
            />
          </Box>

          {/* South (bottom edge) */}
          <Box
            data-testid={`${id}-resize-south`}
            aria-label={`Resize ${label || elementType} height`}
            sx={{
              position: "absolute",
              bottom: 2,
              left: "50%",
              transform: "translateX(-50%)",
              width: 32,
              height: 10,
              bgcolor: "primary.500",
              borderRadius: 4,
              zIndex: 201,
              cursor: "s-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": { bgcolor: "primary.600" },
            }}
            onMouseDown={(e) => handleResizeStart(e, "s")}
          >
            <Box
              sx={{
                width: 16,
                height: 2,
                bgcolor: "white",
                borderRadius: 1,
              }}
            />
          </Box>

          {/* Southeast (corner) */}
          <Box
            data-testid={`${id}-resize-corner`}
            aria-label={`Resize ${label || elementType} width and height`}
            sx={{
              position: "absolute",
              bottom: 2,
              right: 2,
              width: 12,
              height: 12,
              bgcolor: "primary.500",
              borderRadius: "50%",
              zIndex: 202,
              cursor: "se-resize",
              "&:hover": { bgcolor: "primary.600" },
            }}
            onMouseDown={(e) => handleResizeStart(e, "se")}
          />
        </>
      )}

      {/* Size indicator during resize */}
      {resizeState.isResizing && previewSpans && (
        <Box
          sx={{
            position: "absolute",
            bottom: 4,
            right: 4,
            px: 1,
            py: 0.25,
            bgcolor: "primary.500",
            color: "white",
            borderRadius: "sm",
            fontSize: "xs",
            fontWeight: "bold",
          }}
        >
          {previewSpans.columnSpan} × {previewSpans.rowSpan}
        </Box>
      )}
    </Box>
  );
}

export const CanvasGridElement = memo(CanvasGridElementComponent);
export default CanvasGridElement;
